import { Drawer, Flex, Table } from 'antd';
import React, { useCallback } from 'react';
import { useTranslation } from 'translations/hooks';

import {
  DRAWER_WIDTH,
  TABLE_WIDTH,
} from 'business/shift-report/report/pages/ReportEdit/components/mass-validation-drawer/constants';
import {
  useMassValidationColumns,
  useTasksAndData,
} from 'business/shift-report/report/pages/ReportEdit/components/mass-validation-drawer/hooks';
import { Item } from 'business/shift-report/report/pages/ReportEdit/components/mass-validation-drawer/types';
import { ValidationEditableCell } from 'business/shift-report/report/pages/ReportEdit/components/validation-editable-cell';
import { EditableRow } from 'business/shift-report/report/pages/ReportEdit/components/validation-editable-row';
import {
  GetShiftReportMetadataQuery,
  TaskFullFragment,
  Task_Updates,
} from 'generated/graphql';
import { notEmpty } from 'technical/utils/not-empty';
import Button from 'ui/button';

import type { TableProps } from 'antd';

interface MassValidationDrawerProps {
  tasks?: TaskFullFragment[];
  report: NonNullable<GetShiftReportMetadataQuery['shiftReport']>;
  open: boolean;
  onCancel: () => void;
  onValidate: (updateTasks: Task_Updates[]) => void;
}

export const MassValidationDrawer = ({
  tasks,
  report,
  open,
  onCancel,
  onValidate,
}: MassValidationDrawerProps) => {
  const { t } = useTranslation();

  const { selectedTasks, setSelectedTasks, data, setData } = useTasksAndData(
    tasks,
    report,
  );

  const rowSelection: TableProps<
    NonNullable<typeof data>[number]
  >['rowSelection'] = {
    onChange: (selectedKeys: React.Key[], _: NonNullable<Item>[]) => {
      setSelectedTasks(selectedKeys.map((key) => key.toString()));
    },
    type: 'checkbox',
    selectedRowKeys: selectedTasks,
  };

  const handleSave = useCallback(
    (record: Item) => {
      const modifiedIndex = data?.findIndex((item) => item.key === record.key);
      if (modifiedIndex === undefined || modifiedIndex === -1) {
        throw new Error(`record not found in task list ${record.key}`);
      }
      setData(data?.with(modifiedIndex, record));
    },
    [data, setData],
  );

  const mappedToTaskUpdate = (
    data?.map((task) => {
      if (!selectedTasks?.includes(task.key)) {
        return;
      }

      return {
        where: {
          id: { _eq: task.key },
        },
        _set: {
          ring: task.ring,
          startDate: task.start?.toISOString(),
          endDate: task.end?.toISOString(),
          ended: task.ended,
        },
      };
    }) ?? []
  ).filter(notEmpty);

  const columns = useMassValidationColumns(handleSave);

  return (
    <Drawer
      open={open}
      onClose={onCancel}
      footer={
        <Flex justify="flex-end" gap={8}>
          <Button onClick={onCancel}>
            {t('pages.report.massValidation.cancel')}
          </Button>
          <Button
            type="primary"
            onClick={() => onValidate(mappedToTaskUpdate)}
            disabled={mappedToTaskUpdate.length === 0}
          >
            {t('pages.report.massValidation.accept')}
          </Button>
        </Flex>
      }
      width={DRAWER_WIDTH}
    >
      <Table<Item>
        bordered
        rowSelection={rowSelection}
        columns={columns}
        dataSource={data}
        size="small"
        scroll={{ x: TABLE_WIDTH }}
        components={{
          body: {
            row: EditableRow,
            cell: ValidationEditableCell,
          },
        }}
        pagination={{
          pageSize: 15,
        }}
      />
    </Drawer>
  );
};
